<template>
  <div class="user-management">
    <div class="header">
      <h2>用户管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleAddUser">
          <el-icon><Plus /></el-icon>
          添加用户
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="手机号">
          <el-input v-model="searchForm.mobile" placeholder="请输入手机号" clearable />
        </el-form-item>
        <el-form-item label="用户名">
          <el-input v-model="searchForm.realName" placeholder="请输入真实姓名" clearable />
        </el-form-item>
        <el-form-item label="用户类型">
          <el-select v-model="searchForm.role" placeholder="请选择用户类型" clearable>
            <el-option label="债务人" value="debtor" />
            <el-option label="债权人" value="creditor" />
            <el-option label="商户" value="merchant" />
            <el-option label="亲友团" value="friend_group" />
            <el-option label="管理员" value="admin" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="待审核" value="pending" />
            <el-option label="激活" value="active" />
            <el-option label="冻结" value="frozen" />
            <el-option label="禁用" value="inactive" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 用户列表 -->
    <el-card>
      <el-table :data="userList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="mobile" label="手机号" width="120" />
        <el-table-column prop="realName" label="真实姓名" width="120" />
        <el-table-column prop="email" label="邮箱" width="180" />
        <el-table-column prop="role" label="用户类型" width="100">
          <template #default="{ row }">
            <el-tag :type="getUserTypeTag(row.role)">
              {{ getUserTypeText(row.role) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="验证状态" width="120">
          <template #default="{ row }">
            <div class="verification-status">
              <el-tooltip content="身份证验证" placement="top">
                <el-icon :color="row.idCardVerified ? '#67C23A' : '#909399'">
                  <UserFilled v-if="row.idCardVerified" />
                  <User v-else />
                </el-icon>
              </el-tooltip>
              <el-tooltip content="手机验证" placement="top">
                <el-icon :color="row.phoneVerified ? '#67C23A' : '#909399'">
                  <Iphone v-if="row.phoneVerified" />
                  <Phone v-else />
                </el-icon>
              </el-tooltip>
              <el-tooltip content="人脸验证" placement="top">
                <el-icon :color="row.faceVerified ? '#67C23A' : '#909399'">
                  <CameraFilled v-if="row.faceVerified" />
                  <Camera v-else />
                </el-icon>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="注册时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="最后更新" width="180">
          <template #default="{ row }">
            {{ formatDate(row.updatedAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="300" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">
              查看
            </el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button 
              v-if="row.status === 'active'" 
              type="danger" 
              size="small" 
              @click="handleDisable(row)"
            >
              禁用
            </el-button>
            <el-button 
              v-else-if="row.status === 'inactive' || row.status === 'frozen'" 
              type="success" 
              size="small" 
              @click="handleEnable(row)"
            >
              启用
            </el-button>
            <el-button 
              v-if="row.status === 'pending'"
              type="success" 
              size="small" 
              @click="handleApprove(row)"
            >
              审核通过
            </el-button>
            <el-button 
              type="danger" 
              size="small" 
              @click="handleDelete(row)" 
            > 
              删除 
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加/编辑用户对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="500px"
      @close="resetForm"
    >
      <el-form ref="userFormRef" :model="userForm" :rules="userRules" label-width="100px">
        <el-form-item label="手机号" prop="mobile">
          <el-input v-model="userForm.mobile" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="真实姓名" prop="realName">
          <el-input v-model="userForm.realName" placeholder="请输入真实姓名" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="身份证号" prop="idCard">
          <el-input v-model="userForm.idCard" placeholder="请输入身份证号" />
        </el-form-item>
        <el-form-item label="用户类型" prop="role">
          <el-select v-model="userForm.role" placeholder="请选择用户类型">
            <el-option label="债务人" value="debtor" />
            <el-option label="债权人" value="creditor" />
            <el-option label="商户" value="merchant" />
            <el-option label="亲友团" value="friend_group" />
            <el-option label="管理员" value="admin" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="userForm.status" placeholder="请选择状态">
            <el-option label="待审核" value="pending" />
            <el-option label="激活" value="active" />
            <el-option label="冻结" value="frozen" />
            <el-option label="禁用" value="inactive" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="!isEditMode" label="密码" prop="password">
          <el-input v-model="userForm.password" type="password" placeholder="请输入密码" />
        </el-form-item>
        <el-form-item v-else label="密码">
          <el-input 
            v-model="userForm.password" 
            type="password" 
            placeholder="留空则不修改密码" 
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 用户详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="用户详情"
      width="900px"
      v-loading="detailLoading"
    >
      <div v-if="currentUser">
        <!-- 基本信息 -->
        <el-descriptions title="基本信息" :column="3" border>
          <el-descriptions-item label="用户ID">{{ currentUser.id }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ currentUser.mobile }}</el-descriptions-item>
          <el-descriptions-item label="真实姓名">{{ currentUser.realName || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="邮箱">{{ currentUser.email || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="身份证号">{{ currentUser.idCard || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="用户类型">
            <el-tag :type="getUserTypeTag(currentUser.role)">{{ getUserTypeText(currentUser.role) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTag(currentUser.status)">{{ getStatusText(currentUser.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="身份证验证">
            <el-tag :type="currentUser.idCardVerified ? 'success' : 'info'">{{ currentUser.idCardVerified ? '已验证' : '未验证' }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="手机验证">
            <el-tag :type="currentUser.phoneVerified ? 'success' : 'info'">{{ currentUser.phoneVerified ? '已验证' : '未验证' }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="人脸验证">
            <el-tag :type="currentUser.faceVerified ? 'success' : 'info'">{{ currentUser.faceVerified ? '已验证' : '未验证' }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="注册时间">{{ formatDate(currentUser.createdAt) }}</el-descriptions-item>
          <el-descriptions-item label="最后更新">{{ formatDate(currentUser.updatedAt) }}</el-descriptions-item>
        </el-descriptions>

        <!-- 统计信息 -->
        <el-descriptions title="统计信息" :column="3" border class="mt-20" v-if="currentUser.statistics">
          <el-descriptions-item label="我欠的总金额">
            <span class="debt-amount">¥{{ currentUser.statistics.totalDebtAsDebtor?.toFixed(2) || '0.00' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="别人欠我的总金额">
            <span class="credit-amount">¥{{ currentUser.statistics.totalDebtAsCreditor?.toFixed(2) || '0.00' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="我的有效债务数">{{ currentUser.statistics.activeDebtsCountAsDebtor || 0 }} 笔</el-descriptions-item>
          <el-descriptions-item label="别人欠我的债务数">{{ currentUser.statistics.activeDebtsCountAsCreditor || 0 }} 笔</el-descriptions-item>
          <el-descriptions-item label="亲友总数">{{ currentUser.statistics.totalFriendsCount || 0 }} 人</el-descriptions-item>
          <el-descriptions-item label="累计帮还金额">¥{{ currentUser.statistics.totalHelpAmount?.toFixed(2) || '0.00' }}</el-descriptions-item>
        </el-descriptions>

        <!-- 债权人列表（我欠谁的） -->
        <div class="mt-20" v-if="currentUser.debtsAsDebtor && currentUser.debtsAsDebtor.length > 0">
          <h4 class="section-title">我的债权人（我欠谁的）</h4>
          <el-table :data="currentUser.debtsAsDebtor" size="small" border>
            <el-table-column prop="creditorName" label="债权人姓名" width="120" />
            <el-table-column prop="creditorMobile" label="债权人手机" width="130" />
            <el-table-column prop="debtAmount" label="债务金额" width="120">
              <template #default="{ row }">
                <span class="debt-amount">¥{{ parseFloat(row.debtAmount).toFixed(2) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="debtType" label="债务类型" width="100" />
            <el-table-column prop="status" label="状态" width="80">
              <template #default="{ row }">
                <el-tag size="small" :type="row.status === 'active' ? 'success' : 'info'">{{ row.status === 'active' ? '进行中' : row.status }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createdAt" label="创建时间" width="160">
              <template #default="{ row }">{{ formatDate(row.createdAt) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 债务人列表（谁欠我的） -->
        <div class="mt-20" v-if="currentUser.debtsAsCreditor && currentUser.debtsAsCreditor.length > 0">
          <h4 class="section-title">我的债务人（谁欠我的）</h4>
          <el-table :data="currentUser.debtsAsCreditor" size="small" border>
            <el-table-column prop="debtorName" label="债务人姓名" width="120" />
            <el-table-column prop="debtorMobile" label="债务人手机" width="130" />
            <el-table-column prop="debtAmount" label="债务金额" width="120">
              <template #default="{ row }">
                <span class="credit-amount">¥{{ parseFloat(row.debtAmount).toFixed(2) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="debtType" label="债务类型" width="100" />
            <el-table-column prop="status" label="状态" width="80">
              <template #default="{ row }">
                <el-tag size="small" :type="row.status === 'active' ? 'success' : 'info'">{{ row.status === 'active' ? '进行中' : row.status }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createdAt" label="创建时间" width="160">
              <template #default="{ row }">{{ formatDate(row.createdAt) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 亲友团（我添加的亲友） -->
        <div class="mt-20" v-if="currentUser.friendsAdded && currentUser.friendsAdded.length > 0">
          <h4 class="section-title">我的亲友团（我添加的亲友）</h4>
          <el-table :data="currentUser.friendsAdded" size="small" border>
            <el-table-column prop="friendName" label="亲友姓名" width="120" />
            <el-table-column prop="friendMobile" label="手机号" width="130" />
            <el-table-column prop="friendRole" label="角色" width="100">
              <template #default="{ row }">
                <el-tag size="small">{{ getUserTypeText(row.friendRole) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="relationshipType" label="关系类型" width="120" />
            <el-table-column prop="groupType" label="分组" width="80" />
            <el-table-column prop="totalHelpAmount" label="累计帮还" width="100">
              <template #default="{ row }">¥{{ parseFloat(row.totalHelpAmount || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="80">
              <template #default="{ row }">
                <el-tag size="small" :type="row.status === 'accepted' ? 'success' : 'warning'">{{ row.status === 'accepted' ? '已接受' : row.status }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 亲友团（添加我为亲友的） -->
        <div class="mt-20" v-if="currentUser.friendsOf && currentUser.friendsOf.length > 0">
          <h4 class="section-title">我是谁的亲友（添加我为亲友的）</h4>
          <el-table :data="currentUser.friendsOf" size="small" border>
            <el-table-column prop="friendName" label="用户姓名" width="120" />
            <el-table-column prop="friendMobile" label="手机号" width="130" />
            <el-table-column prop="friendRole" label="角色" width="100">
              <template #default="{ row }">
                <el-tag size="small">{{ getUserTypeText(row.friendRole) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="relationshipType" label="关系类型" width="120" />
            <el-table-column prop="groupType" label="分组" width="80" />
            <el-table-column prop="totalHelpAmount" label="累计帮还" width="100">
              <template #default="{ row }">¥{{ parseFloat(row.totalHelpAmount || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="80">
              <template #default="{ row }">
                <el-tag size="small" :type="row.status === 'accepted' ? 'success' : 'warning'">{{ row.status === 'accepted' ? '已接受' : row.status }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 无数据提示 -->
        <div class="no-data-tip" v-if="(!currentUser.debtsAsDebtor || currentUser.debtsAsDebtor.length === 0) && 
                                       (!currentUser.debtsAsCreditor || currentUser.debtsAsCreditor.length === 0) && 
                                       (!currentUser.friendsAdded || currentUser.friendsAdded.length === 0) && 
                                       (!currentUser.friendsOf || currentUser.friendsOf.length === 0)">
          <el-empty description="暂无债务和亲友关系" :image-size="60" />
        </div>
      </div>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEditFromDetail">编辑</el-button>
      </template>
    </el-dialog>

    <!-- 删除确认弹窗 -->
    <el-dialog
      v-model="deleteDialogVisible"
      title="删除用户"
      width="500px"
      :close-on-click-modal="false"
    >
      <div v-if="deletingUser" class="delete-confirm-content">
        <el-alert
          title="注意：此操作将软删除用户"
          type="warning"
          :closable="false"
          show-icon
          class="mb-20"
        >
          <template #default>
            <p>用户将被匿名化处理，手机号将释放可重新注册。</p>
            <p>债务记录和交易记录将保留（显示为"已注销用户"）。</p>
          </template>
        </el-alert>
        
        <el-descriptions :column="1" border class="mb-20">
          <el-descriptions-item label="用户ID">{{ deletingUser.id }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ deletingUser.mobile }}</el-descriptions-item>
          <el-descriptions-item label="真实姓名">{{ deletingUser.realName }}</el-descriptions-item>
          <el-descriptions-item label="用户类型">
            <el-tag :type="getUserTypeTag(deletingUser.role)">{{ getUserTypeText(deletingUser.role) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTag(deletingUser.status)">{{ getStatusText(deletingUser.status) }}</el-tag>
          </el-descriptions-item>
        </el-descriptions>
        
        <el-form label-width="80px">
          <el-form-item label="删除原因" required>
            <el-input 
              v-model="deleteReason" 
              type="textarea" 
              :rows="3"
              placeholder="请输入删除原因（必填）"
            />
          </el-form-item>
        </el-form>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">取消</el-button>
          <el-button 
            type="danger" 
            @click="confirmDelete" 
            :loading="deleteSubmitting"
            :disabled="!deleteReason.trim()"
          >
            确认删除
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Plus, 
  Refresh, 
  Search,
  UserFilled,
  User,
  Iphone,
  Phone,
  CameraFilled,
  Camera
} from '@element-plus/icons-vue'
import { userApi } from '@/api/api.js'

// 状态管理
const loading = ref(false)
const dialogVisible = ref(false)
const detailDialogVisible = ref(false)
const detailLoading = ref(false)
const isEditMode = ref(false)
const submitting = ref(false)
const userFormRef = ref()
const currentUser = ref(null)

const deleteDialogVisible = ref(false)
const deleteReason = ref('')
const deletingUser = ref(null)
const deleteSubmitting = ref(false)


// 搜索表单
const searchForm = reactive({
  mobile: '',
  realName: '',
  role: '',
  status: ''
})

// 用户表单
const userForm = reactive({
  mobile: '',
  realName: '',
  email: '',
  idCard: '',
  role: 'debtor',
  status: 'pending',
  password: ''
})

// 表单验证规则
const userRules = {
  mobile: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
  ],
  realName: [
    { required: true, message: '请输入真实姓名', trigger: 'blur' },
    { min: 2, max: 20, message: '姓名长度在 2 到 20 个字符', trigger: 'blur' }
  ],
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
  ],
  idCard: [
    { required: true, message: '请输入身份证号', trigger: 'blur' },
    { pattern: /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/, 
      message: '请输入正确的身份证号', trigger: 'blur' }
  ],
  role: [
    { required: true, message: '请选择用户类型', trigger: 'change' }
  ],
  status: [
    { required: true, message: '请选择状态', trigger: 'change' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
  ]
}

// 分页配置
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 用户列表数据
const userList = ref([])

// 计算属性
const dialogTitle = computed(() => {
  return isEditMode.value ? '编辑用户' : '添加用户'
})

// 方法
const getUserTypeText = (type) => {
  const map = {
    debtor: '债务人',
    creditor: '债权人',
    merchant: '商户',
    friend_group: '亲友团',
    admin: '管理员'
  }
  return map[type] || type
}

const getUserTypeTag = (type) => {
  const map = {
    debtor: 'warning',
    creditor: 'success',
    merchant: 'info',
    friend_group: '',
    admin: 'danger'
  }
  return map[type] || ''
}

const getStatusText = (status) => {
  const map = {
    pending: '待审核',
    active: '激活',
    frozen: '冻结',
    inactive: '禁用'
  }
  return map[status] || status
}

const getStatusTag = (status) => {
  const map = {
    pending: 'info',
    active: 'success',
    frozen: 'warning',
    inactive: 'danger'
  }
  return map[status] || ''
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

// 获取用户列表
const fetchUsers = async () => {
  loading.value = true
  try {
    // 过滤掉空字符串参数并映射字段名
    const filteredParams = Object.entries(searchForm).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        // 映射前端字段名到API字段名
        const apiKey = key
        acc[apiKey] = value
      }
      return acc
    }, {})
    
    const params = {
      page: pagination.current,
      limit: pagination.size,
      ...filteredParams
    }
    console.log("fetchUsers开始，参数:", params)
    const response = await userApi.getUsers(params)
    console.log("API响应:", response)
    
    // 处理不同的响应格式
    let apiData = {}
    let userListData = []
    
    if (response.data) {
      // 后端标准响应格式: { data: [...], total: 11, ... }
      apiData = response
      userListData = response.data || []
    } else if (response.success && response.data) {
      // 模拟数据格式
      apiData = response.data
      userListData = apiData.data || []
    } else if (Array.isArray(response)) {
      // 直接返回数组的情况
      userListData = response
    } else {
      // 其他情况
      userListData = []
    }
    
    // 映射API数据到前端期望的格式
    const mappedUserList = userListData.map(user => ({
      id: user.id,
      mobile: user.mobile,
      realName: user.realName || '未设置',
      email: user.email || '未设置',
      role: user.role || 'debtor',
      status: user.status || 'pending',
      idCardVerified: user.idCardVerified || false,
      phoneVerified: user.phoneVerified || true,
      faceVerified: user.faceVerified || false,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt || user.createdAt,
      _raw: user
    }))
    console.log("映射后的用户列表:", mappedUserList)
    
    // 使用API返回的真实数据
    if (mappedUserList.length > 0) {
      console.log("使用API返回的真实数据")
      userList.value = mappedUserList
      pagination.total = apiData.total || userListData.length || 0
    } else {
      // 如果API返回空数据，使用模拟数据作为 fallback
      console.log("API返回空数据，使用模拟数据作为 fallback")
      userList.value = [
        {
          id: 1,
          mobile: '13800138001',
          realName: '张三',
          email: 'zhangsan@example.com',
          role: 'debtor',
          status: 'active',
          idCardVerified: true,
          phoneVerified: true,
          faceVerified: false,
          createdAt: '2026-03-01T10:00:00Z',
          updatedAt: '2026-03-02T10:00:00Z'
        },
        {
          id: 2,
          mobile: '13800138002',
          realName: '李四',
          email: 'lisi@example.com',
          role: 'creditor',
          status: 'active',
          idCardVerified: true,
          phoneVerified: true,
          faceVerified: true,
          createdAt: '2026-03-01T11:00:00Z',
          updatedAt: '2026-03-02T11:00:00Z'
        },
        {
          id: 3,
          mobile: '13800138003',
          realName: '王五',
          email: 'wangwu@example.com',
          role: 'merchant',
          status: 'active',
          idCardVerified: true,
          phoneVerified: true,
          faceVerified: false,
          createdAt: '2026-03-01T12:00:00Z',
          updatedAt: '2026-03-02T12:00:00Z'
        },
        {
          id: 4,
          mobile: '13800138004',
          realName: '赵六',
          email: 'zhaoliu@example.com',
          role: 'friend_group',
          status: 'pending',
          idCardVerified: false,
          phoneVerified: true,
          faceVerified: false,
          createdAt: '2026-03-01T13:00:00Z',
          updatedAt: '2026-03-01T13:00:00Z'
        },
        {
          id: 5,
          mobile: '13800138005',
          realName: '钱七',
          email: 'qianqi@example.com',
          role: 'admin',
          status: 'active',
          idCardVerified: true,
          phoneVerified: true,
          faceVerified: true,
          createdAt: '2026-03-01T14:00:00Z',
          updatedAt: '2026-03-02T14:00:00Z'
        }
      ]
      pagination.total = 5
    }
    
  } catch (error) {
    console.error('获取用户列表失败:', error)
    ElMessage.error('获取用户列表失败')
    
    // 错误时使用模拟数据
    userList.value = [
      {
        id: 1,
        mobile: '13800138001',
        realName: '张三',
        email: 'zhangsan@example.com',
        role: 'debtor',
        status: 'active',
        idCardVerified: true,
        phoneVerified: true,
        faceVerified: false,
        createdAt: '2026-03-01T10:00:00Z',
        updatedAt: '2026-03-02T10:00:00Z'
      },
      {
        id: 2,
        mobile: '13800138002',
        realName: '李四',
        email: 'lisi@example.com',
        role: 'creditor',
        status: 'active',
        idCardVerified: true,
        phoneVerified: true,
        faceVerified: true,
        createdAt: '2026-03-01T11:00:00Z',
        updatedAt: '2026-03-02T11:00:00Z'
      },
      {
        id: 3,
        mobile: '13800138003',
        realName: '王五',
        email: 'wangwu@example.com',
        role: 'merchant',
        status: 'active',
        idCardVerified: true,
        phoneVerified: true,
        faceVerified: false,
        createdAt: '2026-03-01T12:00:00Z',
        updatedAt: '2026-03-02T12:00:00Z'
      },
      {
        id: 4,
        mobile: '13800138004',
        realName: '赵六',
        email: 'zhaoliu@example.com',
        role: 'friend_group',
        status: 'pending',
        idCardVerified: false,
        phoneVerified: true,
        faceVerified: false,
        createdAt: '2026-03-01T13:00:00Z',
        updatedAt: '2026-03-01T13:00:00Z'
      },
      {
        id: 5,
        mobile: '13800138005',
        realName: '钱七',
        email: 'qianqi@example.com',
        role: 'admin',
        status: 'active',
        idCardVerified: true,
        phoneVerified: true,
        faceVerified: true,
        createdAt: '2026-03-01T14:00:00Z',
        updatedAt: '2026-03-02T14:00:00Z'
      }
    ]
    pagination.total = 5
  } finally {
    loading.value = false
  }
}

const refreshData = () => {
  fetchUsers()
}

const handleSearch = () => {
  pagination.current = 1
  fetchUsers()
}

const resetSearch = () => {
  Object.keys(searchForm).forEach(key => {
    searchForm[key] = ''
  })
  handleSearch()
}

const handleSizeChange = (size) => {
  pagination.size = size
  fetchUsers()
}

const handleCurrentChange = (page) => {
  pagination.current = page
  fetchUsers()
}

const handleAddUser = () => {
  isEditMode.value = false
  dialogVisible.value = true
}

const handleEdit = (row) => {
  isEditMode.value = true
  Object.assign(userForm, row)
  userForm.password = '' // 编辑时不显示密码
  dialogVisible.value = true
}

const handleView = async (row) => {
  detailLoading.value = true
  detailDialogVisible.value = true
  try {
    const response = await userApi.getUserDetail(row.id)
    if (response.code === 200 || response) {
      currentUser.value = response.data || response
    } else {
      currentUser.value = row
    }
  } catch (error) {
    console.error('获取用户详情失败:', error)
    currentUser.value = row
  } finally {
    detailLoading.value = false
  }
}

const handleEditFromDetail = () => {
  if (currentUser.value) {
    detailDialogVisible.value = false
    handleEdit(currentUser.value)
  }
}
const handleApprove = async (row) => {
  try {
    await ElMessageBox.confirm('确定要审核通过该用户吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    loading.value = true
    await userApi.updateUserStatus(row.id, 'active')
    ElMessage.success('用户审核通过')
    fetchUsers()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('审核用户失败:', error)
      ElMessage.error('审核用户失败')
    }
  } finally {
    loading.value = false
  }
}

const handleDisable = async (row) => {
  try {
    await ElMessageBox.confirm('确定要禁用该用户吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    loading.value = true
    await userApi.updateUserStatus(row.id, 'inactive')
    ElMessage.success('用户已禁用')
    fetchUsers()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('禁用用户失败:', error)
      ElMessage.error('禁用用户失败')
    }
  } finally {
    loading.value = false
  }
}

const handleEnable = async (row) => {
  try {
    await ElMessageBox.confirm('确定要启用该用户吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    loading.value = true
    await userApi.updateUserStatus(row.id, 'active')
    ElMessage.success('用户已启用')
    fetchUsers()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('启用用户失败:', error)
      ElMessage.error('启用用户失败')
    }
  } finally {
    loading.value = false
  }
}

const handleDelete = async (row) => {
  // 打开删除确认弹窗
  deletingUser.value = row
  deleteReason.value = ''
  deleteDialogVisible.value = true
}

const confirmDelete = async () => {
  if (!deleteReason.value.trim()) {
    ElMessage.warning('请输入删除原因')
    return
  }
  
  deleteSubmitting.value = true
  try {
    const response = await userApi.deleteUser(deletingUser.value.id, deleteReason.value)
    if (response.code === 200 || response.message === '用户已注销') {
      // 显示警告信息
      if (response.warnings) {
        const warnings = []
        if (response.warnings.hadActiveDebts) {
          warnings.push()
        }
        if (response.warnings.hadCredits) {
          warnings.push()
        }
        if (warnings.length > 0) {
          ElMessage.warning(warnings.join('；'))
        } else {
          ElMessage.success('用户已注销')
        }
      } else {
        ElMessage.success('用户已注销')
      }
      deleteDialogVisible.value = false
      fetchUsers()
    } else {
      ElMessage.error(response.message || '删除失败')
    }
  } catch (error) {
    console.error('删除用户失败:', error)
    ElMessage.error('删除用户失败')
  } finally {
    deleteSubmitting.value = false
  }
}

const resetForm = () => {
  if (userFormRef.value) {
    userFormRef.value.resetFields()
  }
  Object.keys(userForm).forEach(key => {
    if (key !== 'role' && key !== 'status') {
      userForm[key] = ''
    }
  })
  userForm.role = 'debtor'
  userForm.status = 'pending'
}

const handleSubmit = async () => {
  if (!userFormRef.value) return
  
  try {
    await userFormRef.value.validate()
    
    submitting.value = true
    
    if (isEditMode.value) {
      // 更新用户
      const { id, ...updateData } = userForm
      if (!updateData.password) {
        delete updateData.password
      }
      await userApi.updateUser(id, updateData)
      ElMessage.success('用户更新成功')
    } else {
      // 添加用户
      await userApi.createUser(userForm)
      ElMessage.success('用户添加成功')
    }
    
    dialogVisible.value = false
    fetchUsers()
    resetForm()
  } catch (error) {
    console.error('保存用户失败:', error)
    ElMessage.error(error.response?.data?.message || '保存用户失败')
  } finally {
    submitting.value = false
  }
}

// 生命周期
onMounted(() => {
  fetchUsers()
})
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 24px;
  color: #ffffff;
}

.actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.verification-status {
  display: flex;
  gap: 8px;
  align-items: center;
}

.verification-status .el-icon {
  font-size: 16px;
}

/* 详情对话框样式 */
.mt-20 {
  margin-top: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
  padding-left: 10px;
  border-left: 3px solid #409eff;
}

.debt-amount {
  color: #f56c6c;
  font-weight: 600;
}

.credit-amount {
  color: #67c23a;
  font-weight: 600;
}

.no-data-tip {
  margin-top: 20px;
}

.delete-confirm-content {
  padding: 10px 0;
}
.delete-confirm-content .mb-20 {
  margin-bottom: 20px;
}
</style>
